import { useRef, useState, useEffect, useContext } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Trophy, BookOpen, Mic, Star, Award, Loader2, ChevronDown, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import axios from "axios";
import { SiteContext } from "@/App";

const COLLAPSED_COUNT = 5;
const PAGE_SIZE = 10;

const TYPE_ICONS = {
  Award: Trophy,
  Publication: BookOpen,
  Feature: Mic,
  Milestone: Star,
  Education: Award,
};

const TYPE_STYLES = {
  Award: "text-verde border-verde/30 bg-verde/10",
  Publication: "text-blue-400 border-blue-400/30 bg-blue-400/10",
  Feature: "text-amber-400 border-amber-400/30 bg-amber-400/10",
  Milestone: "text-purple-400 border-purple-400/30 bg-purple-400/10",
  Education: "text-[#A3A3A3] border-[#404040] bg-ink-800",
};

const PARTNER_LOGOS = {
  "matrix cartridges": {
    url: "https://matrixcartridges.com",
    logo: "/images/matrix-cartridges-logo.jpg",
    name: "Matrix Cartridges",
  },
};

function getPartnerInfo(title) {
  const lower = (title || "").toLowerCase();
  for (const [key, info] of Object.entries(PARTNER_LOGOS)) {
    if (lower.includes(key)) return info;
  }
  return null;
}

function PartnerCard({ item, index, inView }) {
  const partner = getPartnerInfo(item.title);

  return (
    <motion.a
      href={partner?.url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.2 + index * 0.15, duration: 0.5 }}
      data-testid={`partner-card-${index}`}
      className="group relative block overflow-hidden rounded-sm border border-verde/20 bg-gradient-to-br from-ink-800 via-ink-900 to-ink-950 hover:border-verde/50 transition-all duration-500"
    >
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-verde/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative p-6 md:p-8 flex items-center gap-6">
        {/* Logo */}
        <div className="w-16 h-16 md:w-20 md:h-20 flex-shrink-0 rounded-sm bg-ink-950 border border-ink-600 flex items-center justify-center overflow-hidden group-hover:border-verde/30 transition-colors duration-500">
          {partner?.logo ? (
            <img
              src={partner.logo}
              alt={partner.name}
              className="w-full h-full object-contain p-1"
            />
          ) : (
            <Star size={24} className="text-verde" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-manrope text-[8px] tracking-[0.3em] uppercase text-verde border border-verde/30 bg-verde/10 px-2 py-0.5 rounded-sm">
              Official Partner
            </span>
          </div>
          <h3 className="font-cinzel text-lg md:text-xl font-bold text-[#E5E5E5] group-hover:text-verde transition-colors duration-300 mb-1">
            {item.title}
          </h3>
          {item.event && (
            <p className="font-manrope text-xs text-[#525252]">{item.event}</p>
          )}
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <ExternalLink size={16} className="text-verde" />
        </div>
      </div>

      {/* Bottom accent line */}
      <div className="h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-verde/80 to-verde/20 transition-all duration-500" />
    </motion.a>
  );
}

function AccoladeRow({ item, i, inView }) {
  const Icon = TYPE_ICONS[item.type] || Trophy;
  return (
    <motion.div
      key={item.id || i}
      initial={{ opacity: 0, x: -20 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: Math.min(i * 0.05, 0.4), duration: 0.45 }}
      data-testid={`accolade-item-${i}`}
      className="accolade-item border-b border-ink-600 py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-8 cursor-default group"
    >
      <div className="flex-shrink-0 w-16">
        <p className="font-cinzel font-bold text-xl text-[#A3A3A3] group-hover:text-verde transition-colors duration-300">
          {item.year}
        </p>
      </div>

      <div className="flex-shrink-0 hidden md:block">
        <Icon size={16} className="text-[#404040] group-hover:text-verde transition-colors duration-300" />
      </div>

      <div className="flex-1">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <h3 className={`font-cinzel text-sm md:text-base font-bold transition-colors duration-300 ${item.featured ? "text-[#E5E5E5]" : "text-[#A3A3A3]"} group-hover:text-[#E5E5E5]`}>
            {item.title}
          </h3>
          <span className={`font-manrope text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border w-fit ${TYPE_STYLES[item.type] || TYPE_STYLES["Award"]}`}>
            {item.type}
          </span>
        </div>
        <p className="font-manrope text-xs text-[#525252] mt-1">
          {item.event}
          {item.location && item.location !== "Publication" && item.location !== "Worldwide" && (
            <span className="ml-2 text-[#404040]">— {item.location}</span>
          )}
        </p>
      </div>

      {item.featured && (
        <div className="flex-shrink-0">
          <Star size={14} className="text-verde fill-verde/30" />
        </div>
      )}
    </motion.div>
  );
}

export default function AccoladesList() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { API } = useContext(SiteContext);
  const [accolades, setAccolades] = useState([]);
  const [ongoing, setOngoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    axios.get(`${API}/accolades`)
      .then(r => {
        const all = r.data || [];
        const ongoingItems = all.filter(a => a.year === "Ongoing");
        const dated = all
          .filter(a => a.year !== "Ongoing")
          .sort((a, b) => (b.year || "0").localeCompare(a.year || "0"));
        setOngoing(ongoingItems);
        setAccolades(dated);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  const totalPages = Math.ceil(accolades.length / PAGE_SIZE);
  const visibleItems = expanded
    ? accolades.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : accolades.slice(0, COLLAPSED_COUNT);

  const handleExpand = () => {
    setExpanded(true);
    setPage(1);
  };

  const handleCollapse = () => {
    setExpanded(false);
    setPage(1);
    document.getElementById("accolades")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="accolades-section" ref={ref} className="py-24 md:py-32 bg-ink-900 relative overflow-hidden">
      {/* Ambient background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.07]"
        style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_lud-hub/artifacts/8aeag4qp_DSC01681.jpeg)` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/80 to-ink-900" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-manrope text-verde text-xs tracking-[0.3em] uppercase mb-4">Recognition</p>
          <div className="section-divider mb-8" />
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-[#E5E5E5] leading-tight">
              Awards &amp;<br />Accolades
            </h2>
            <p className="font-manrope text-[#525252] text-sm max-w-sm leading-relaxed">
              Over a decade of international recognition in Black &amp; Grey Realism tattooing, from Vancouver to the world stage.
            </p>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-verde" />
          </div>
        ) : (
          <>
            {/* Ongoing Partnerships — Sports Team Style */}
            {ongoing.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="mb-16"
              >
                <p className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-6">
                  Official Partnerships
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ongoing.map((item, i) => (
                    <PartnerCard key={item.id || i} item={item} index={i} inView={inView} />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Dated Accolades Timeline */}
            <div className="space-y-0 border-t border-ink-600">
              <AnimatePresence mode="wait">
                {visibleItems.map((item, i) => (
                  <AccoladeRow key={item.id || i} item={item} i={i} inView={inView} />
                ))}
              </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="mt-10 flex flex-col items-center gap-6">
              {!expanded && accolades.length > COLLAPSED_COUNT && (
                <motion.button
                  data-testid="accolades-show-more"
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.5 }}
                  onClick={handleExpand}
                  className="flex items-center gap-3 btn-ghost group"
                >
                  <span>Show All {accolades.length} Accolades</span>
                  <ChevronDown size={14} className="group-hover:translate-y-0.5 transition-transform" />
                </motion.button>
              )}

              {expanded && totalPages > 1 && (
                <div data-testid="accolades-pagination" className="flex items-center gap-2">
                  <button
                    data-testid="accolades-prev"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 border border-ink-600 text-[#A3A3A3] hover:border-verde hover:text-verde disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-sm"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      data-testid={`accolades-page-${p}`}
                      onClick={() => setPage(p)}
                      className={`w-9 h-9 font-manrope text-xs font-semibold border rounded-sm transition-colors ${
                        p === page
                          ? "bg-verde text-black border-verde"
                          : "border-ink-600 text-[#A3A3A3] hover:border-verde hover:text-verde"
                      }`}
                    >
                      {p}
                    </button>
                  ))}

                  <button
                    data-testid="accolades-next"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 border border-ink-600 text-[#A3A3A3] hover:border-verde hover:text-verde disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-sm"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {expanded && (
                <button
                  data-testid="accolades-collapse"
                  onClick={handleCollapse}
                  className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] hover:text-verde transition-colors"
                >
                  Show Less
                </button>
              )}
            </div>
          </>
        )}

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="font-italiana text-2xl text-[#404040] mt-12 text-center italic"
        >
          &ldquo;Art that outlasts everything.&rdquo;
        </motion.p>
      </div>
    </section>
  );
}
